import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

const state = {
  staff: [],
  formStatuses: [
    { id: -1, name: 'Deleted' },
    { id: 1, name: 'New' },
    { id: 10, name: 'Submitted' },
    { id: 20, name: 'Completed' },
    { id: 30, name: 'Closed' }
  ],
  kycList: [],
  totalForms: 0,
  formsPerPage: 25,
  currentPage: 1,
  noSearchResults: false,
  searched: false,
  isSearching: false,
  listShowCompletedForms: true,
  staffIdFilterBy: '',
  statusIdFilterBy: '',
  searchQuery: null,
  docTypesDesc: null,
  isLoading: false,
  shareThreshold: 25.01,
  companyTypes: [
    {
      name: 'individual_type',
      description: 'Individual'
    },
    {
      name: 'hk_company',
      description: 'Hong Kong private company'
    },
    {
      name: 'hk_listed',
      description: 'Listed Company or Regulated Company'
    },
    {
      name: 'prc_company',
      description: 'PRC company'
    },
    {
      name: 'non_hk_company',
      description: 'Non HK company (excluding PRC)'
    },
    {
      name: 'law_firm',
      description: 'Law firm'
    }
  ]
}

const mutations = {
  setStaff (state, staff) {
    state.staff = staff
  },
  clearStaff (state) {
    state.staff = []
  },
  setKycList (state, kycList) {
    state.kycList = kycList
  },
  clearKycList (state) {
    state.kycList = []
  },
  setTotalForms (state, total) {
    state.totalForms = total
  },
  clearTotalForms (state) {
    state.totalForms = 0
  },
  setSearched (state, searched) {
    state.searched = searched
  },
  setNoSearchResults (state, noSearchResults) {
    state.noSearchResults = noSearchResults
  },
  setIsSearching (state, isSearching) {
    state.isSearching = isSearching
  },
  setShowCompleted (state, showCompleted) {
    state.listShowCompletedForms = showCompleted
  },
  setStaffIdFilterBy (state, staffIdFilterBy) {
    state.staffIdFilterBy = staffIdFilterBy
  },
  setStatusIdFilterBy (state, statusIdFilterBy) {
    state.statusIdFilterBy = statusIdFilterBy
  },
  setSearchQuery (state, q) {
    state.searchQuery = q
  },
  clearSearchQuery (state) {
    state.searchQuery = null
  },
  setDocTypesDesc (state, docTypesDesc) {
    state.docTypesDesc = docTypesDesc
  },
  setCurrentPage (state, page) {
    state.currentPage = page
  },
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },
  setCompanyTypes (state, companyTypes) {
    state.companyTypes = companyTypes
  }
}

// actions are functions that cause side effects and can involve
// asynchronous operations.
const actions = {
  clearStaff: ({ commit }) => commit('clearStaff'),
  getStaff: ({ commit }) => {
    const staff = localStorage.getItem('staff')
    const lastUpdated = localStorage.getItem('lastupdate')
    const timeNow = (new Date()).getTime()

    if (staff === null || (timeNow - lastUpdated) > 1000 * 60 * 60 * 12) {
      Vue.axios.get('/wfm/staff')
        .then(response => {
          if (response.data) {
            if (response.data.hasOwnProperty('Error') && response.data.Error) {
              console.log(response.data.Error)
            } else {
              commit('setStaff', response.data)
              localStorage.setItem('staff', JSON.stringify(response.data))
              localStorage.setItem('lastupdate', (new Date()).getTime())
            }
          }
        })
        .catch(error => {
          console.log(error)
        })
    } else {
      commit('setStaff', JSON.parse(staff))
    }
  },
  getKycList: ({ commit }) => {
    commit('setIsLoading', true)
    let params = { c: state.listShowCompletedForms | 0 }

    if (state.staffIdFilterBy) {
      params['staff_id'] = state.staffIdFilterBy
    }

    if (state.statusIdFilterBy) {
      params['status'] = state.statusIdFilterBy
    }

    params['page'] = state.currentPage ? state.currentPage : 1

    Vue.axios.get('/kyc/all', { params })
      .then(response => {
        commit('setIsLoading', false)
        if (response.data) {
          if (response.data.hasOwnProperty('Error') && response.data.Error) {
            console.log(response.data.Error)
          } else {
            if (response.data.forms === undefined) {
              commit('setKycList', [])
              commit('setTotalForms', 0)
            }
            commit('setKycList', response.data.forms)
            commit('setTotalForms', response.data.total)
          }
        }
      })
      .catch(error => {
        console.log(error)
      })
  },
  searchKyc: ({ commit }) => {
    const q = state.searchQuery
    const c = state.listShowCompletedForms | 0
    const page = state.currentPage ? state.currentPage : 1

    // commit('setIsSearching', true)

    Vue.axios.post('/kyc/search', { q, c, page })
      .then(response => {
        if (response.data) {
          commit('setKycList', response.data.forms)
          commit('setTotalForms', response.data.total)
          commit('setSearched', true)
          commit('setIsSearching', false)
          if (response.data.total === 0) commit('setNoSearchResults', true)
        } else {
          commit('setNoSearchResults', true)
        }
      })
      .catch(error => {
        console.log(error)
      })
  },
  getDocTypesDesc: ({ commit }) => {
    Vue.axios.get('/document-types')
      .then(response => {
        commit('setDocTypesDesc', response.data)
      })
      .catch(error => {
        console.log(error)
      })
  },
  getCompanyTypes: ({ commit }) => {
    Vue.axios.get('/entity-types')
      .then(response => {
        commit('setCompanyTypes', response.data)
      })
      .catch(error => {
        console.log(error)
      })
  },
  clearKyc: ({ commit }) => {
    commit('clearKycList')
  },
  setTotalForms: ({ commit }, { total }) => {
    commit('setTotalForms', total)
  },
  clearTotalForms: ({ commit }) => {
    commit('clearTotalForms')
  },
  setSearched: ({ commit }, { searched }) => {
    commit('setSearched', searched)
  },
  setNoSearchResults: ({ commit }, { noSearchResults }) => {
    commit('setNoSearchResults', noSearchResults)
  },
  setShowCompleted: ({ commit }, { showCompleted }) => {
    commit('setShowCompleted', showCompleted)
  },
  setStaffIdFilterBy: ({ commit }, { staffIdFilterBy }) => {
    commit('setStaffIdFilterBy', staffIdFilterBy)
  },
  setStatusIdFilterBy: ({ commit }, { statusIdFilterBy }) => {
    commit('setStatusIdFilterBy', statusIdFilterBy)
  },
  setSearchQuery: ({ commit }, { q }) => {
    commit('setSearchQuery', q)
  },
  clearSearchQuery: ({ commit }) => {
    commit('clearSearchQuery')
  },
  setCurrentPage: ({ commit }, { page }) => {
    commit('setCurrentPage', page)
  },
  setIsLoading: ({ commit }, { isLoading }) => {
    commit('setIsLoading', isLoading)
  },
  setIsSearching: ({ commit }, { isSearching }) => {
    commit('setIsSearching', isSearching)
  }
}

const getters = {
  staffNames: state => {
    return state.staff.map(s => s.name)
  },
  getStaffByName: (state) => (name) => {
    return state.staff.find(s => s.name === name)
  },
  getStaffById: (state) => (id) => {
    return state.staff.find(s => s.id === id.toString())
  },
  getStaffNameById: (state) => (id) => {
    if (!id) return
    const staff = state.staff.find(s => s.id === id.toString())
    if (staff === undefined) return
    return staff.name
  },
  getFormStatusName: (state) => (value) => {
    if (value === undefined) return
    const status = state.formStatuses.find(s => s.id === value)
    if (status === undefined) return
    return status.name
  },
  getCompanyTypeByTypeName: (state) => (name) => {
    if (!name) return null
    if (name === 'individual') name = 'individual_type'
    const companyType = state.companyTypes.find(c => c.name === name)
    return companyType ? companyType.description : name
  }
}

export default new Vuex.Store({
  state,
  getters,
  actions,
  mutations
})
